'use client';

import { Item, ItemImage } from '@/api';
import ProductRelated from '@/components/ecommerce/product/ProductRelated';
import ItemContext from '@/contexts/ItemContext';
import { getItemQueryFn } from '@/queries/item';
import { useQuery } from '@tanstack/react-query';
import { useParams, useSearchParams, useRouter, usePathname } from 'next/navigation';
import { useEffect, useMemo, useRef, useState } from 'react';
import Lightbox from 'yet-another-react-lightbox';
import Video from 'yet-another-react-lightbox/plugins/video';
import Zoom from 'yet-another-react-lightbox/plugins/zoom';
import i18next from 'i18n';
import useAuction from '@/hooks/useAuction';
import { useAlert } from '@/providers/AlertProvider';
import ProductBidHistory from '@/components/ecommerce/product/ProductBidHistory';
import ProductDescription from '@/components/ecommerce/product/ProductDescription';
import Container from '@/components/Layout/Container';
import ProductBid, { BidLeadingState } from './ProductBid/ProductBid';
import ProductImageStrip from './ProductImageStrip';

export default function ProductPage({ ghostLoading, item: serverLoadedItem }: { ghostLoading?: boolean, item?: Item }) {
    const { slug } = useParams();
    const { show } = useAlert();
    const router = useRouter();
    const pathname = usePathname();
    const queryParams = useSearchParams();

    useEffect(() => {
        if (queryParams.get('buyError')) {
            show({
                text: i18next.t('error-text.buy-error'),
                animation: 'AlertAnimation',
                buttonText: i18next.t('button-text.got-it'),
                description: queryParams.get('buyError') || '',
                showButton: true,
                action: () => router.replace(pathname)
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const { data: loadedItem, isInitialLoading, refetch } = useQuery({
        queryKey: ['getItemQueryFn', serverLoadedItem?.slug || slug],
        queryFn: () => getItemQueryFn(slug as string, true),
        refetchOnWindowFocus: true,
        enabled: !ghostLoading,
        initialData: serverLoadedItem,
        initialDataUpdatedAt: Date.now(),
        refetchOnMount: false
    });

    const item = loadedItem || serverLoadedItem;
    const isLoading = !item && isInitialLoading;

    const [index, setIndex] = useState<number>(-1);

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const [lastBidState, setLastBidState] = useState<BidLeadingState>();

    const { comingSoon } = useAuction(item, undefined, false);

    const memoizedItem = useMemo(() => ({
        item,
        refetch
    }), [item, refetch]);

    const auctionStartTimeout = useRef<NodeJS.Timeout>();

    useEffect(() => {
        if (comingSoon) {
            const offsetTimeForReload = 10 * 1000;
            const reminderTime = new Date(
                comingSoon.startTime.getTime() + offsetTimeForReload
            );
            const timeUntilReload = reminderTime.getTime() - new Date().getTime();
            if (auctionStartTimeout.current) {
                clearTimeout(auctionStartTimeout.current);
            }
            auctionStartTimeout.current = setTimeout(() => {
                refetch();
            }, timeUntilReload);
            return () => {
                clearTimeout(auctionStartTimeout.current);
            };
        }
    }, [refetch, comingSoon]);

    return (
        <div className="bg-white h-full">
            <ItemContext.Provider value={memoizedItem || null}>
                <div className="border-b-[1px] mb-[20px] border-grey9">
                    <Container className="py-4 md:py-[16px] !px-0 md:!px-4">
                        <ProductBid product={item} onImageClicked={(selectedImageIndex: number) => setIndex(selectedImageIndex)} isLoading={isLoading || ghostLoading} onBidStateChange={setLastBidState} />
                    </Container>
                </div>

                {isLoading || ghostLoading ? null : (
                    <>
                        <div className="border-b-[1px] mb-[40px] border-grey9">
                            <div className="flex max-w-outer mx-auto mb-[20px] md:mb-[40px] p-[30px] space-x-[0px] flex-col lg:flex-row space-y-[50px] lg:!space-y-[0px]  lg:!space-x-[50px] items-start">
                                <ProductDescription />
                                {item?.current_auction && <ProductBidHistory />}
                            </div>
                        </div>
                        <ProductImageStrip />
                        {item && <ProductRelated />}
                        <Lightbox
                            index={index}
                            plugins={[Video, Zoom]}
                            zoom={{ maxZoomPixelRatio: 2 }}
                            slides={item?.images.map((image) => {
                                if (image.type === ItemImage.TypeEnum.Image) {
                                    return { type: 'image', src: image.url };
                                }
                                return { type: 'video', sources: [{ src: image.url, type: 'video/mp4' }], autoPlay: true, preload: 'auto', loop: true };
                            })}
                            open={index >= 0}
                            close={() => setIndex(-1)}
                        />
                    </>
                )}
            </ItemContext.Provider>
        </div>
    );
}
